import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  ButtonProps,
  CircularProgress,
  Menu,
  MenuItem
} from '@material-ui/core';
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons';

import AppSnackbar from '../../../common/AppSnackbar';
import { User } from '../../../user/User.interface';
import { useExportTrainee } from '../Training.hooks';

export interface TraineeExportButtonProps {
  user: User | undefined;
  buttonProps?: ButtonProps;
}

const TraineeExportButton: React.FC<TraineeExportButtonProps> = (
  props: TraineeExportButtonProps
) => {
  const { user, buttonProps } = props;
  const extensionRef = useRef<'docx' | 'pdf'>('docx');
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [
    { isLoading, error, response },
    getDocument,
    downloadDocument
  ] = useExportTrainee(user?.id || 0);

  const getDocumentAs = (extension: 'docx' | 'pdf') => {
    extensionRef.current = extension;
    getDocument(extension);
  };

  useEffect(() => {
    if (response?.ok && user) {
      const { firstName, lastName } = user;
      downloadDocument(
        `Employee Training Record - ${firstName} ${lastName}.${extensionRef.current}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, response]);

  return (
    <>
      <AppSnackbar severity='error' open={!!error}>
        {error?.message || 'An error occurred while exporting the document.'}
      </AppSnackbar>

      <Button
        color='primary'
        variant='contained'
        endIcon={
          isLoading ? (
            <CircularProgress color='primary' size={20} />
          ) : (
            <ArrowDropDownIcon />
          )
        }
        {...buttonProps}
        disabled={isLoading || buttonProps?.disabled}
        onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          setMenuAnchorEl(event.currentTarget);
        }}
      >
        Export Training Record
      </Button>

      <Menu
        id='trainee-export-menu'
        keepMounted
        open={!!menuAnchorEl}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            getDocumentAs('docx');
          }}
        >
          Export as DOCX
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            getDocumentAs('pdf');
          }}
        >
          Export as PDF
        </MenuItem>
      </Menu>
    </>
  );
};

export default TraineeExportButton;
